import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/AuthContext";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../config/firebase";
import { getNamHocActive, getActiveHocKy } from "../../services/namHocService";
import { getCache, setCache } from "../../services/cacheService";

const AdminDashboard = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const today = new Date();

  const [stats, setStats] = useState({ tongHS: 0, tongLop: 0, tongGV: 0, tyLeCC: 0 });
  const [loading, setLoading] = useState(true);
  const [namHoc, setNamHoc] = useState("2025-2026");
  const [hocKy, setHocKy] = useState("HK1");

  useEffect(() => {
    const init = async () => {
      try { const nh = await getNamHocActive(); if (nh) setNamHoc(nh.tenNamHoc || nh.id); } catch(e) {}
      try { const hk = await getActiveHocKy(); if (hk) setHocKy(hk.hocKy); } catch(e) {}
    };
    init();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const cacheKey = `admin_dashboard_light_${namHoc}_${hocKy}`;
      const cached = getCache(cacheKey);
      if (cached) { setStats(cached); setLoading(false); return; }

      setLoading(true);
      try {
        const [hsSnap, lopSnap, userSnap, ddSnap] = await Promise.all([
          getDocs(collection(db, "hocSinh")),
          getDocs(collection(db, "lopHoc")),
          getDocs(collection(db, "nguoiDung")),
          getDocs(collection(db, "diemDanh")),
        ]);

        const ddList = ddSnap.docs.map(d => d.data()).filter(d => {
          if (!d.ngayHoc) return false;
          const m = new Date(d.ngayHoc).getMonth() + 1;
          const y = new Date(d.ngayHoc).getFullYear();
          const [namBD] = namHoc.split('-');
          const namKT = namBD ? parseInt(namBD) + 1 : 2026;
          if (!((y === parseInt(namBD) && m >= 8) || (y === namKT && m <= 5))) return false;
          return hocKy === 'HK1' ? (m >= 8 && m <= 12) : (m >= 1 && m <= 5);
        });

        const tongBuoi = [...new Set(ddList.map(d => d.ngayHoc))].length;
        const coMat = ddList.filter(d => d.trangThai === 'coMat' || d.trangThai === 'diTre').length;
        const tyLeCC = hsSnap.size > 0 && tongBuoi > 0 ? Math.round((coMat / (hsSnap.size * tongBuoi)) * 100) : 0;

        const newStats = { tongHS: hsSnap.size, tongLop: lopSnap.size, tongGV: userSnap.size, tyLeCC };
        setStats(newStats);
        setCache(cacheKey, newStats);
      } catch(e) { console.error(e); }
      setLoading(false);
    };
    fetchData();
  }, [namHoc, hocKy]);

  const formatDate = () => today.toLocaleDateString('vi-VN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  if (loading) return <div className="flex items-center justify-center h-64"><span className="material-symbols-outlined text-primary animate-spin text-4xl">sync</span></div>;

  return (
    <div className="space-y-5">
      {/* WELCOME */}
      <section className="flex flex-col md:flex-row justify-between items-start md:items-end gap-3">
        <div>
          <h1 className="text-[24px] md:text-[28px] font-bold text-primary">Xin chào, Admin {user?.tenThanh ? `${user?.tenThanh} ` : ''}{user?.hoTen || '...'}</h1>
          <p className="text-body-md text-on-surface-variant mt-1">{namHoc} • {hocKy}</p>
        </div>
        <div className="text-caption text-on-surface-variant bg-surface-container-low px-3 py-1.5 rounded-full flex items-center gap-1">
          <span className="material-symbols-outlined text-sm">calendar_today</span>{formatDate()}
        </div>
      </section>

      {/* 4 STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
        {[
          { icon: 'groups', label: 'Tổng HS', value: stats.tongHS, color: 'text-primary', bg: 'bg-primary-container', path: '/quan-ly-hoc-sinh' },
          { icon: 'school', label: 'Lớp học', value: stats.tongLop, color: 'text-secondary', bg: 'bg-secondary-container', path: '/cai-dat' },
          { icon: 'manage_accounts', label: 'Giáo viên', value: stats.tongGV, color: 'text-tertiary', bg: 'bg-tertiary-container', path: '/quan-ly-nguoi-dung' },
          { icon: 'trending_up', label: 'CC toàn trường', value: `${stats.tyLeCC}%`, color: stats.tyLeCC>=90?'text-green-600':stats.tyLeCC>=70?'text-yellow-600':'text-error', bg: stats.tyLeCC>=90?'bg-green-100':stats.tyLeCC>=70?'bg-yellow-100':'bg-error-container' },
        ].map((c,i) => (
          <button key={i} onClick={() => c.path && navigate(c.path)}
            className={`bg-surface rounded-xl p-4 shadow-sm border border-outline-variant flex flex-col justify-between text-left ${c.path ? 'hover:shadow-md transition-all active:scale-[0.98] cursor-pointer' : ''}`}>
            <div className="flex justify-between items-start mb-2"><span className="text-body-md text-on-surface-variant">{c.label}</span><div className={`p-1.5 rounded-full ${c.bg}`}><span className={`material-symbols-outlined ${c.color} text-xl`}>{c.icon}</span></div></div>
            <div className={`text-[32px] font-bold ${c.color}`}>{c.value}</div>
          </button>
        ))}
      </div>

      {/* QUICK ACTIONS */}
      <section>
        <h2 className="text-section-title text-primary mb-3 font-bold">Thao tác nhanh</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { icon: 'person_add', label: 'Thêm HS', path: '/quan-ly-hoc-sinh', color: 'bg-primary' },
            { icon: 'manage_accounts', label: 'Thêm User', path: '/quan-ly-nguoi-dung', color: 'bg-secondary' },
            { icon: 'settings', label: 'Cài đặt', path: '/cai-dat', color: 'bg-tertiary' },
            { icon: 'group', label: 'QL Học sinh', path: '/quan-ly-hoc-sinh', color: 'bg-[#181b23]' },
          ].map((a,i) => (
            <button key={i} onClick={() => navigate(a.path)} className={`${a.color} text-on-primary rounded-xl p-4 flex flex-col items-center justify-center gap-2 hover:opacity-90 active:scale-95`}>
              <span className="material-symbols-outlined text-3xl">{a.icon}</span>
              <span className="text-body-lg font-semibold text-center">{a.label}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;